import cache from 'data/apollo-cache';
import {useApolloLoaderContext} from 'data/apollo-loading-context';
import {useQuery, useMutation, gql as runTimeGQL} from '@apollo/client';
import {usePageContext} from 'data/page-context';
import {useViewer} from 'data/queries/viewer';
import renameKeys from 'helpers/renameKeys';

const ALL_VIEWER_ACTIONS = runTimeGQL`
query AllViewerActions {
  viewer {
    mbActions {
      nodes {
        actionType
        courseId
        id
        pageId
        payload
        timestamp
        topicId
      }
    }
  }
}
`;

const SYNC_ACTIONS = runTimeGQL`
mutation SyncActions($actions: [MBActionsSyncAction!]!) {
  mbActionsSync(input:{
    mbActions: $actions,
  }) {
    errors {
      message
      name
    }
    success
    updatedUser {
      id
      mbActions {
        nodes {
          id
          actionType
          courseId
          pageId
          payload
          timestamp
          topicId
        }
      }
    }
  }
}
`;

const UNSYNCED_ACTIONS = runTimeGQL`
query UnsyncedActions {
  actions @client
}
`;

const useAllActions = () => {
  const {apolloLoading} = useApolloLoaderContext();
  if (apolloLoading) return [];

  const {data} = useQuery(ALL_VIEWER_ACTIONS);
  const syncedActions = data?.viewer?.mbActions?.nodes || [];
  const cachedActions = useQuery(UNSYNCED_ACTIONS)?.data?.actions || [];
  return cachedActions.concat(syncedActions);
};

const clearUnsyncedActions = () => {
  cache.writeQuery({
    query: UNSYNCED_ACTIONS,
    data: {actions: []},
  });
};

const setupAnalyticsIdentity = ({loading, data}) => {
  if (data?.viewer) {
    const {
      viewer: {databaseId: userId, firstName: name, email},
    } = data;

    // if no analytics identity, set identity
    if (window.analytics && window.analytics.user().id() !== userId) {
      window.analytics.identify({
        userId,
        traits: {
          name,
          email,
        },
      });
    }
  } else {
    const needsAnalyticsResetAfterLogout =
      navigator.onLine && // able to connect
      !loading && // finished attempt to connect
      window.analytics && // analytics loaded in
      window.analytics.user().traits().name; // name previously set (logged in before)

    if (needsAnalyticsResetAfterLogout) {
      window.analytics.reset();
    }
  }
};

const useSyncUnsyncedActions = () => {
  const [syncActions] = useMutation(SYNC_ACTIONS);
  const {loading, data} = useViewer('network-only');
  return () => {
    setupAnalyticsIdentity({loading, data});
    if (navigator.onLine && !loading && data?.viewer) {
      // if we're online and signed in, attempt to sync unsynced actions
      const cachedActions = cache.readQuery({query: UNSYNCED_ACTIONS})?.actions || [];
      if (cachedActions.length) {
        // eslint-disable-next-line no-console
        console.log(`syncing ${cachedActions.length} unsynced actions with the server`);
        syncActions({variables: {actions: cachedActions}}).then(
          response => {
            if (response?.data?.mbActionsSync?.success) {
              // eslint-disable-next-line no-console
              console.log(`synced ${cachedActions.length}, clearing cache`);
              // soon but not yet
              setTimeout(clearUnsyncedActions);
            } else {
              // eslint-disable-next-line no-console
              console.error('Issue syncing, graphql response: ', response);
            }
          },
          err => {
            // eslint-disable-next-line no-console
            console.error('Syncing error', err);
          }
        );
      }
    } else if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('not syncing: ', {
        'navigator.onLine': navigator.onLine,
        loading,
        'data.viewer': data?.viewer,
      });
    }
  };
};

const useSafeCreateActions = () => {
  const syncUnsyncedActions = useSyncUnsyncedActions();
  return actions => {
    const cachedData = cache.readQuery({query: UNSYNCED_ACTIONS});
    const data = {actions: actions.concat(cachedData?.actions || [])};
    cache.writeQuery({
      query: UNSYNCED_ACTIONS,
      data,
    });
    syncUnsyncedActions();
  };
};

const makeTimestamp = () => new Date().toISOString();

const trackAction = action => {
  const {type, courseDbId, topicDbId, pageDbId, timestamp, payload} = action;
  if (window.analytics) {
    window.analytics.track(
      `Action ${type}`,
      renameKeys(payload, 'Action', {
        'Course DB ID': courseDbId,
        'Topic DB ID': topicDbId,
        'Page DB ID': pageDbId,
        'Client Timestamp': timestamp,
      })
    );
  }
};

const useCreateAction = () => {
  const pageContext = usePageContext();
  const safeCreateActions = useSafeCreateActions();
  return (type, payload = {}) => {
    const action = {
      actionType: type,
      timestamp: makeTimestamp(),
      courseId: pageContext.page?.course?.dbId,
      topicId: pageContext.page?.topic?.dbId,
      pageId: pageContext.page?.dbId,
      payload,
    };
    trackAction(action);
    safeCreateActions([action]);
  };
};

export {useCreateAction, useAllActions, useSyncUnsyncedActions};
